import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import { Login } from '../../../supports/login';
import unifiedAuthManager from '../../../services/unifiedAuthManager';
const LoginPage = ({ hideSignUpLink = false }) => {

  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isPasswordFocused, setIsPasswordFocused] = useState(false);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState('');

  const validateEmail = (value) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(value);
  };

  const location = useLocation();
const isResumeLogin = /^\/resume-login\/?$/.test(location.pathname);

  const handleEmailChange = (value) => {
    setEmail(value);
    if (!value) {
      setEmailError('');
    } else if (!validateEmail(value)) {
      setEmailError('Please enter a valid email address.');
    } else {
      setEmailError('');
    }
    setApiError('');
  };

  const handlePasswordChange = (value) => {
    setPassword(value);
    if (passwordError) {
      setPasswordError('');
    }
    setApiError('');
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    let hasErrors = false;

    if (!email.trim()) {
      setEmailError('Email is required.');
      hasErrors = true;
    } else if (!validateEmail(email)) {
      setEmailError('Please enter a valid email address.');
      hasErrors = true;
    }

    if (!password) {
      setPasswordError('Password is required.');
      hasErrors = true;
    }

    if (hasErrors) {
      return;
    }

    setLoading(true);
    setApiError('');
    try {
      // Replace with your actual API call
      const response = await Login.login({
        username: email,
        password,
        user_payload: {
          request_id: `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
        }
      });

      if (response.success) {
        console.log("Login successful:", response);
        const firstname = response.data?.firstname || "";
        const lastname = response.data?.lastname || "";
        const roles = response.data?.role || [];
        const candidateId = response.data?.candidate_id || response.data?.candidateId || null;
        const resumeId = response.data?.resume_id || response.data?.resumeId || null;
        const broadbeanUserName = response.data?.broadbean_username?.[0] ?? null;
        unifiedAuthManager.storeCustomApiResponse({
          id: response.data?.id,
          email: response.data.email,
          tenant_id: response.data?.tenant_id || '',
          jtoken: response.data?.jtoken || '',
          user_roles: roles,
          tenant: response.data?.tenant || '',
          name: `${firstname} ${lastname}`.trim(),
          candidate_id: candidateId,
          resume_id: resumeId,
          broadbean_username:broadbeanUserName

        });

        console.log("candidate:", roles);

        //role priority = admin > hr > candidate
        //role contains admin then show all jobs in home page and show the admindashboard instead of leaderboard
        //role contains role_HR show recruiter
        //role contains role_candidate show the candidate portal
        if(response.data.change_password_flag && candidateId ){
          navigate('/resumeForgotPassword');
          return;
        }
        else if(response.data.change_password_flag && !candidateId ){
          navigate('/forgotPassword');
          return;
        }
        if (roles.includes("ROLE_ADMIN") && !isResumeLogin){
          navigate('/adminDashboard')
        }
        else if (roles.includes("ROLE_HR") && !isResumeLogin) {
          navigate("/recruiterDashboard");
        }
         else if (roles.includes("ROLE_CANDIDATEUSER") && isResumeLogin) {
          console.log("Navigating to candidate route1");
          if (candidateId && resumeId) {
            navigate(`/job-recommendations?candidateId=${candidateId}`);
          } else {
            navigate("/profiler");
          }
        } else {
          // console.log("Navigating to candidate route2");
          // navigate("/dashboard");
          if (roles.includes("ROLE_USER") && !isResumeLogin){
            unifiedAuthManager.clearAll()
            setApiError('You don’t have permission to access this application. Please contact your administrator.');
          }
          else{
            unifiedAuthManager.clearAll()
            setApiError('No account found for the entered email.');
          }
        }
      } else {
        setApiError(response.message || "Invalid email or password.");
      }
    } catch (err) {
      console.log('login error:', err);
      setApiError("An error occurred while signing in. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const getInputClassName = (fieldName, fieldError) => {
    const baseClasses = "mt-1 block h-11 min-h-[44px] w-full appearance-none px-4 py-2.5 text-sm leading-5 rounded-md shadow-sm placeholder:text-gray-999 placeholder:font-semibold";
    if (fieldError) {
      return `${baseClasses} border-red-500 focus:border-red-500 border`;
    }
    return `${baseClasses} border border-gray-300`;
  };

  return (
    <div className="w-full max-w-md p-8">
      {/* Company Logo and Header */}
      <div className="text-center mb-8">
        <img src={require('../../../assets/RecruiterLogo.png')} alt="MPH" className="w-[130px] mx-auto mb-4" />
        {/* <span className='profiler text-5xl font-semibold font-bold mb-5'>Profiler</span> */}
        <h2 className="text-lg font-bold text-gray-333 mt-5">Sign into your account</h2>
      </div>

      {/* Login Form */}
      <form onSubmit={handleSignIn} className="text-left">
        <div className="form-group">
          <label htmlFor="email" className="block text-sm font-bold text-gray-333">Email</label>
          <input
            type="email"
            id="email"
            value={email}
            onChange={(e) => handleEmailChange(e.target.value)}
            placeholder="Enter your email" autoComplete='off'
            className={getInputClassName('email', emailError)}
            required
            aria-invalid={emailError ? 'true' : 'false'}
            aria-describedby={emailError ? 'login-email-error' : undefined}
            disabled={loading}
          />
          {emailError && (
            <p id="login-email-error" className="mt-1 text-xs text-red-600">{emailError}</p>
          )}
        </div>
        <div className="form-group mt-4">
          <label htmlFor="password" className="block text-sm font-bold text-gray-333">Password</label>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              id="password"
              value={password} autoComplete='off'
              onChange={(e) => handlePasswordChange(e.target.value)}
              onFocus={() => setIsPasswordFocused(true)}
              onBlur={() => setIsPasswordFocused(false)}
              placeholder="Enter your password"
              className={`${getInputClassName('password', passwordError)} pr-10`}
              required
              aria-invalid={passwordError ? 'true' : 'false'}
              aria-describedby={passwordError ? 'login-password-error' : undefined}
              disabled={loading}
            />
            {(password || isPasswordFocused) && (
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute inset-y-0 right-0 flex h-11 w-11 items-center justify-center text-base text-gray-333 hover:text-gray-999 z-10"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                tabIndex={-1}
              >
                {showPassword ? <FiEyeOff /> : <FiEye />}
              </button>
            )}
          </div>
          {passwordError && (
            <p id="login-password-error" className="mt-1 text-xs text-red-600">{passwordError}</p>
          )}
        </div>

        {apiError && (
          <div className="mt-3 text-xs text-red-600 text-center">{apiError}</div>
        )}

        <div className="text-right mt-2">
          {isResumeLogin && (
            <Link to="/resumeForgotPassword" className="text-sm text-gray-999 hover:text-emerald-800">
            Forgot password?
          </Link>
            )}
          {!isResumeLogin && (
            <Link to="/forgotPassword" className="text-sm text-gray-999 hover:text-emerald-800">
            Forgot password?
          </Link>
          )}
     
        </div>

        <button
          type="submit"
          disabled={Boolean(emailError || passwordError || loading)}
          className={`mt-4 w-full min-h-[44px] flex justify-center items-center py-2.5 px-4 border rounded-md shadow-sm text-sm font-medium  ${(emailError || passwordError || loading) ? 'bg-gray-300 cursor-not-allowed' : 'profiler-bg'}`}
        >
          {loading ? "Signing in..." : "Sign in"}
        </button>
      </form>

      {/* Sign Up Link */}
      {isResumeLogin ? (
        <div className="mt-4 flex flex-wrap justify-center items-center gap-x-1 text-center text-sm text-gray-999">
          <span className="whitespace-nowrap">Don't have an account?</span>
          <span className="whitespace-nowrap">
            <Link
              to="/profiler"
              className="font-medium mph-primary hover:text-emerald-800 mr-1"
            >
              Upload Resume
            </Link>
            Or
            <Link to='/candidate_signup' className="font-medium mph-primary hover:text-emerald-800 ml-1">
              Sign Up
            </Link>
          </span>
        </div>
    
      ) : (
        !hideSignUpLink && (
          <p className="mt-4 text-center text-sm text-gray-999">
            Don't have an account?{" "}
            <Link
              to="/signup"
              className="font-medium mph-primary hover:text-emerald-800"
            >
              Sign Up
            </Link>
          </p>
        )
      )}
    </div>
  );
};

export default LoginPage;
